import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/store';
import { useGetRegistrationQuery } from '../../../services/RegistrationService';
import { CardContent, Stack, Box, Typography, Alert, Chip, Divider, Button } from '@mui/material';
import { PersonOutline, HomeOutlined, PhoneOutlined, EmailOutlined, CalendarTodayOutlined, BadgeOutlined, EditOutlined, LocalHospitalOutlined } from '@mui/icons-material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import { EncountersForPatientGrid } from '../components/grids';
import { AddEncounterButton } from '../components/commands';
import { PageLoading as Loading } from '../../common/components';
import SectionPaper from '../../common/components/styled-ui-elements/SectionPaper';
import PageCard from '../../common/components/styled-ui-elements/PageCard';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const RegistrationContent: React.FC = () => {
  const query = useQuery();
  const company = query.get('company') || '';
  const center = query.get('center') || '';
  const mrn = query.get('mrn') || '';
  const navigate = useNavigate();
  const registration = useAppSelector((state) => state.registration.currentRegistration);

  // Create concatenated ID for API call
  const registrationId = `${company}-${center}-${mrn}`;

  // Use RTK Query to fetch registration data
  const { error, isLoading } = useGetRegistrationQuery({ id: registrationId }, {
    skip: !company || !center || !mrn,
  });

  // Redirect to create page if required params are missing
  useEffect(() => {
    if (!company || !center || !mrn) {
      navigate('/RegistrationCreate');
      return;
    }
  }, [company, center, mrn, navigate]);

  // Show loading state
  if (isLoading) {
    return <Loading />;
  }

  // Show error state
  if (error) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Alert severity="error">
          Failed to load registration data. Please try again.
        </Alert>
      </Box>
    );
  }

  if (!registration) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Alert severity="info">
          No registration data found.
        </Alert>
      </Box>
    );
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPhone = (phone: string | undefined) => {
    if (!phone) return 'Not provided';
    // Simple phone formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: 3,
        px: 2
      }}
    >
      <Box display="flex" justifyContent="center">
        <PageCard sx={{ maxWidth: 900 }}>
          {/* Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}
          >
            <Box>
              <Typography variant="h4" component="h1" fontWeight={600} color="#1e293b" sx={{ mb: 1 }}>
                {registration.name?.last && registration.name?.first 
                  ? `${registration.name.last}, ${registration.name.first} (${mrn})`
                  : `Registration (${mrn})`
                }
              </Typography>
              <Typography variant="body1" color="#64748b">
                Center: {center} • Company: {company}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => navigate(`/RegistrationEdit?company=${company}&center=${center}&mrn=${mrn}`)}
                sx={{
                  color: '#162346',
                  borderColor: '#162346',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#162346',
                    color: 'white'
                  }
                }}
              >
                Edit Registration
              </Button>
              <GoBackButton />
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* Main Content Layout */}
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              {/* Patient Information */}
              <Box flex={1}>
                <SectionPaper sx={{ p: 3, borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <PersonOutline sx={{ mr: 1, color: '#3b82f6', fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                      Patient Information
                    </Typography>
                  </Box>
                  
                  <Stack spacing={2}>
                    {/* Contact Information Row */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Primary Phone
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <PhoneOutlined sx={{ mr: 1, fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body1" color="#1e293b">
                            {formatPhone(registration.contactInfo?.phone)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Email Address
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <EmailOutlined sx={{ mr: 1, fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body1" color="#1e293b">
                            {registration.contactInfo?.email || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Alternate Phone
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <PhoneOutlined sx={{ mr: 1, fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body1" color="#1e293b">
                            {formatPhone(registration.contactInfo?.alternatePhone)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Date of Birth
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <CalendarTodayOutlined sx={{ mr: 1, fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body1" color="#1e293b">
                            {formatDate(registration.demographics?.dateOfBirth)}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>

                    {/* Demographics Row */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Gender
                        </Typography>
                        <Chip 
                          label={registration.demographics?.gender || 'Not specified'} 
                          size="small"
                          sx={{ backgroundColor: '#e0f2fe', color: '#0277bd' }}
                        />
                      </Box>
                      
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Race
                        </Typography>
                        <Typography variant="body1" color="#1e293b">
                          {registration.demographics?.race || 'Not provided'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="#64748b" sx={{ mb: 0.5 }}>
                          Marital Status
                        </Typography>
                        <Typography variant="body1" color="#1e293b">
                          {registration.demographics?.maritalStatus || 'Not provided'}
                        </Typography>
                      </Box>
                      <Box flex={1}></Box>
                    </Stack>
                  </Stack>
                </SectionPaper>
              </Box>

              {/* Address Information */}
              {registration.addresses && registration.addresses.length > 0 && (
                <Box flex={1}>
                  <SectionPaper sx={{ p: 3, borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <HomeOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 24 }} />
                      <Typography variant="h6" fontWeight={600} color="#1e293b">
                        Address Information
                      </Typography>
                    </Box>
                    
                    <Stack spacing={2}>
                      {registration.addresses.map((address, index) => (
                        <Box key={index}>
                          {index > 0 && <Divider sx={{ my: 2 }} />}
                          <Box display="flex" justifyContent="space-between" alignItems="start" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" color="#64748b">
                              {address.addressType || 'Address'}
                            </Typography>
                            {address.isPreferred && (
                              <Chip 
                                label="Preferred" 
                                size="small"
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body1" color="#1e293b">
                            {[
                              address.street,
                              address.street2,
                              `${address.city}, ${address.state} ${address.zip}`
                            ].filter(Boolean).join(', ') || 'Address not provided'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </SectionPaper>
                </Box>
              )}
            </Stack>

            {/* Encounters Section */}
            <SectionPaper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center">
                  <LocalHospitalOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={600} color="#1e293b">
                    Encounters
                  </Typography>
                </Box>
                
                <AddEncounterButton company={company} center={center} mrn={mrn} />
              </Box>
              
              <EncountersForPatientGrid />
            </SectionPaper>
          </CardContent>
        </PageCard>
      </Box>
    </Box>
  );
};

const Registration: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <RegistrationContent />
    </AuthenticatedTemplate>
  );
};

export default Registration;
